/* ==========================================================================
   KR4NTI theme.js
   Vanilla JS, no dependencies, loaded with `defer`.
   Everything here enhances markup that already works without JavaScript:
   forms post to Shopify's own /cart routes, links go to real pages.
   Cart: Shopify AJAX Cart API (/cart/add.js, /cart/change.js) + Section Rendering
   API, so prices and totals are always rendered by Shopify, never computed here.
   ========================================================================== */
(() => {
  'use strict';

  const T = window.theme || { routes: {}, strings: {} };
  const R = T.routes;
  const S = T.strings;

  /* ---------- utilities ---------- */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const escapeHTML = (str) =>
    String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  function announce(message) {
    const region = document.getElementById('LiveRegion');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 50);
  }

  async function requestJSON(url, options = {}, timeout = 15000) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) },
      });
      let data = null;
      try {
        data = await response.json();
      } catch (e) {
        data = null;
      }
      return { ok: response.ok, status: response.status, data };
    } catch (error) {
      return { ok: false, status: 0, data: null, network: true };
    } finally {
      window.clearTimeout(timer);
    }
  }

  function errorText(result) {
    if (result.network) return S.cartNetworkError;
    const data = result.data || {};
    if (typeof data.description === 'string' && data.description) return data.description;
    if (typeof data.message === 'string' && data.message && data.message !== 'Cart Error') return data.message;
    return S.cartError;
  }

  /* ---------- dialogs (menu, search, cart, size guide) ---------- */
  const Dialogs = {
    open(id, trigger) {
      const dialog = document.getElementById(id);
      if (!dialog || typeof dialog.showModal !== 'function') return false;
      dialog._trigger = trigger || document.activeElement;
      if (!dialog._bound) {
        dialog._bound = true;
        dialog.addEventListener('close', () => {
          const t = dialog._trigger;
          if (t && document.contains(t) && typeof t.focus === 'function') t.focus({ preventScroll: true });
        });
      }
      if (!dialog.open) dialog.showModal();
      return true;
    },
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      if (Dialogs.open(opener.dataset.dialogOpen, opener)) event.preventDefault();
      return;
    }
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) dialog.close();
      return;
    }
    // A click on the backdrop lands on the <dialog> itself, outside its box.
    if (event.target.tagName === 'DIALOG' && event.target.open) {
      const r = event.target.getBoundingClientRect();
      const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
      if (!inside) event.target.close();
    }
  });

  /* ---------- cart ---------- */
  const CartState = { busy: false, pending: null };

  const Cart = {
    hasDrawer() {
      return T.cartType === 'drawer' && !!document.getElementById('CartDrawer');
    },
    sectionIds() {
      const ids = [];
      if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
      $$('cart-items[data-section]').forEach((el) => {
        if (el.dataset.section !== 'cart-drawer' && !ids.includes(el.dataset.section)) ids.push(el.dataset.section);
      });
      return ids;
    },
    add(variantId, quantity = 1) {
      return requestJSON(`${R.cartAdd}.js`, {
        method: 'POST',
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity }],
          sections: this.sectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });
    },
    change(key, quantity) {
      return requestJSON(`${R.cartChange}.js`, {
        method: 'POST',
        body: JSON.stringify({
          id: key,
          quantity,
          sections: this.sectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });
    },
    render(sections) {
      if (!sections) return;
      let root = null;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const fresh = parseHTML(html).querySelector('[data-cart-root]');
        if (!fresh) return;
        const current =
          id === 'cart-drawer'
            ? document.querySelector('#CartDrawer [data-cart-root]')
            : document.querySelector(`#shopify-section-${CSS.escape(id)} [data-cart-root]`);
        if (current) {
          current.replaceWith(fresh);
          root = fresh;
        }
      });
      if (root) this.updateCount(Number(root.dataset.itemCount), root.dataset.countText);
      showArrival();
      markLoadedImages();
    },
    updateCount(count, text) {
      if (Number.isNaN(count)) return;
      $$('[data-cart-bubble]').forEach((bubble) => {
        bubble.textContent = count;
        bubble.dataset.count = count;
      });
      if (text) $$('[data-cart-count-text]').forEach((el) => (el.textContent = text));
    },
    status(message, isError = false) {
      $$('[data-cart-status]').forEach((slot) => {
        const root = slot.closest('[data-cart-root]');
        const icon = root && $(isError ? 'template[data-icon-alert]' : 'template[data-icon-check]', root);
        slot.innerHTML = `<p class="cart-status${isError ? ' cart-status--error' : ''}" role="status" tabindex="-1">${
          icon ? icon.innerHTML : ''
        }<span>${escapeHTML(message)}</span></p>`;
      });
    },
    open(trigger, message) {
      const drawer = document.getElementById('CartDrawer');
      if (!this.hasDrawer() || !drawer) {
        window.location.href = R.cart;
        return;
      }
      const wasOpen = drawer.open;
      Dialogs.open('CartDrawer', trigger);
      if (message) {
        this.status(message);
        announce(message);
        if (wasOpen) {
          const status = $('#CartDrawer .cart-status');
          if (status) status.focus({ preventScroll: false });
        }
      }
    },
    addedMessage(data) {
      const item = data && data.items && data.items[0];
      if (!item) return S.cartUpdated;
      const name = item.variant_title ? `${item.product_title} (${item.variant_title})` : item.product_title;
      return S.cartAddedItem.replace('[title]', name);
    },
  };

  // Header cart link opens the drawer instead of loading /cart.
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-cart-toggle]');
    if (!toggle || !Cart.hasDrawer()) return;
    event.preventDefault();
    Cart.open(toggle);
  });

  // Visible feedback while the checkout POST navigates away (no blocking, no delay).
  document.addEventListener('submit', (event) => {
    const button = event.submitter;
    if (button && button.matches('[data-checkout]')) button.setAttribute('aria-busy', 'true');
  });

  /* ---------- <variant-picker> ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const json = this.querySelector('script[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.single = this.hasAttribute('data-single');
      this.form = this.closest('form');
      this.groups = $$('[data-option-group]', this);
      if (!this.form) return;

      // From here on a single hidden input carries the matched variant id. It stays disabled
      // (so nothing is submitted) until a real variant is matched.
      this.idInput = document.createElement('input');
      this.idInput.type = 'hidden';
      this.idInput.name = 'id';
      this.idInput.disabled = true;
      this.form.appendChild(this.idInput);
      if (this.single) {
        $$('input[type="radio"]', this).forEach((radio) => {
          radio.name = `${this.dataset.idPrefix}-option-0`;
        });
      } else {
        const select = $('[data-native-select]', this);
        if (select) {
          select.disabled = true;
          select.removeAttribute('name');
        }
      }

      this.addEventListener('change', (event) => {
        if (!event.target.matches('input[type="radio"]')) return;
        this.clearError(event.target.closest('[data-option-group]'));
        this.update(true);
      });
      this.update(false);
    }

    selectedValues() {
      return this.groups.map((group) => {
        const checked = $('input[type="radio"]:checked', group);
        return checked ? checked.dataset.value : null;
      });
    }

    get variant() {
      const values = this.selectedValues();
      if (values.some((v) => v === null)) return null;
      return this.variants.find((variant) => variant.options.every((opt, i) => opt === values[i])) || null;
    }

    update(userAction) {
      const values = this.selectedValues();
      if (!this.single) {
        // Mark values that cannot make an available variant with the other current choices.
        this.groups.forEach((group, gi) => {
          $$('input[type="radio"]', group).forEach((radio) => {
            const possible = this.variants.some(
              (variant) =>
                variant.available &&
                variant.options[gi] === radio.dataset.value &&
                variant.options.every((opt, oi) => oi === gi || values[oi] === null || values[oi] === opt)
            );
            radio.dataset.unavailable = possible ? 'false' : 'true';
            radio.setAttribute('aria-label', possible ? radio.dataset.value : `${radio.dataset.value}, ${S.unavailable}`);
          });
        });
      }
      this.groups.forEach((group, gi) => {
        const label = $('[data-option-value]', group);
        if (label) label.textContent = values[gi] === null ? S.selectSize : values[gi];
      });
      const variant = this.variant;
      const complete = values.every((v) => v !== null);
      if (this.idInput) {
        this.idInput.value = variant ? variant.id : '';
        this.idInput.disabled = !variant;
      }
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant, complete, userAction } }));
    }

    /** Returns { ok, variant } or { ok: false, reason, message } and shows inline errors. */
    validate() {
      const values = this.selectedValues();
      let firstMissing = null;
      this.groups.forEach((group, gi) => {
        if (values[gi] === null) {
          this.showError(group);
          if (!firstMissing) firstMissing = group;
        }
      });
      if (firstMissing) {
        firstMissing.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
        const first = $('input[type="radio"]:not(:disabled)', firstMissing);
        if (first) first.focus({ preventScroll: true });
        const msg = $('[data-option-error] span', firstMissing);
        return { ok: false, reason: 'missing', message: msg ? msg.textContent.trim() : S.selectSize };
      }
      const variant = this.variant;
      if (!variant) return { ok: false, reason: 'combination', message: S.unavailableCombination };
      if (!variant.available) return { ok: false, reason: 'soldout', message: S.soldOut };
      return { ok: true, variant };
    }

    showError(group) {
      group.classList.remove('has-error');
      void group.offsetWidth; // restart the nudge animation
      group.classList.add('has-error');
      const error = $('[data-option-error]', group);
      if (!error) return;
      error.hidden = false;
      $$('input[type="radio"]', group).forEach((radio) => {
        radio.setAttribute('aria-describedby', error.id);
        radio.setAttribute('aria-invalid', 'true');
      });
    }

    clearError(group) {
      if (!group) return;
      group.classList.remove('has-error');
      const error = $('[data-option-error]', group);
      if (error) error.hidden = true;
      $$('input[type="radio"]', group).forEach((radio) => {
        radio.removeAttribute('aria-describedby');
        radio.removeAttribute('aria-invalid');
      });
    }
  }

  /* ---------- <product-form> (product page + compact suggestion forms) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.form = $('form', this);
      if (!this.form) return;
      this.form.noValidate = true; // validation is handled inline below
      this.button = $('[data-add-button]', this.form);
      this.label = $('[data-add-label]', this.form);
      this.error = $('[data-form-error]', this.form);
      this.dynamic = $('[data-dynamic-checkout]', this.form);
      this.isMain = this.hasAttribute('data-main');
      this.defaultLabel = this.label ? this.label.textContent.trim() : S.addToCart;
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('variant:change', (event) => this.onVariantChange(event.detail));
      // Buy it now must not start without a valid size either.
      if (this.dynamic) this.dynamic.addEventListener('click', (event) => this.guardDynamic(event), true);
    }

    get picker() {
      return $('variant-picker', this.form);
    }

    onVariantChange({ variant, complete, userAction }) {
      this.hideError();
      if (this.button && !this.busy) {
        const blocked = complete && (!variant || !variant.available);
        this.button.disabled = blocked;
        if (this.label) this.label.textContent = blocked ? (variant ? S.soldOut : S.unavailable) : this.defaultLabel;
      }
      if (this.dynamic) this.dynamic.hidden = !(variant && variant.available);
      if (!this.isMain) return;

      const priceEl = document.querySelector('[data-product-price]');
      if (priceEl && variant) {
        priceEl.innerHTML = variant.compare
          ? `<span class="price" data-price><span class="visually-hidden">${escapeHTML(S.salePrice)}</span><span class="price__sale">${escapeHTML(
              variant.price
            )}</span><span class="visually-hidden">${escapeHTML(S.regularPrice)}</span><s class="price__compare">${escapeHTML(
              variant.compare
            )}</s></span>`
          : `<span class="price" data-price><span>${escapeHTML(variant.price)}</span></span>`;
      }
      const tagEl = document.querySelector('[data-product-tag]');
      if (tagEl && variant) {
        let tag = '';
        if (!variant.available) tag = `<span class="sale-tag sale-tag--out">${escapeHTML(S.soldOut)}</span>`;
        else if (variant.discount) tag = `<span class="sale-tag">${escapeHTML(S.saleTag.replace('[percent]', variant.discount))}</span>`;
        tagEl.innerHTML = tag;
      }
      if (userAction && variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      }
      document.dispatchEvent(new CustomEvent('product:variant', { detail: { variant, formId: this.form.id } }));
    }

    guardDynamic(event) {
      const picker = this.picker;
      if (!picker) return;
      const variant = picker.variant;
      if (!variant || !variant.available) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const result = picker.validate();
        if (!result.ok && result.reason !== 'missing') this.showError(result.message);
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy) return; // no duplicate submissions from repeated taps
      this.hideError();

      let variantId = null;
      const picker = this.picker;
      if (picker) {
        const result = picker.validate();
        if (!result.ok) {
          if (result.reason !== 'missing') this.showError(result.message);
          announce(result.message);
          return;
        }
        variantId = result.variant.id;
      } else {
        const input = $('input[name="id"]', this.form);
        variantId = input ? input.value : null;
      }
      if (!variantId) {
        this.showError(S.cartError);
        return;
      }
      const qtyInput = $('input[name="quantity"]', this.form);
      const quantity = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);

      if (!Cart.hasDrawer()) {
        // Cart page mode: Shopify's own form post, which redirects to /cart.
        HTMLFormElement.prototype.submit.call(this.form);
        return;
      }

      this.setBusy(true);
      const result = await Cart.add(variantId, quantity);
      this.setBusy(false);
      if (!result.ok) {
        const message = errorText(result);
        this.showError(message);
        announce(message);
        return;
      }
      Cart.render(result.data.sections);
      Cart.open(this.button, Cart.addedMessage(result.data));
    }

    setBusy(busy) {
      this.busy = busy;
      if (!this.button) return;
      if (busy) this.button.setAttribute('aria-busy', 'true');
      else this.button.removeAttribute('aria-busy');
      if (this.label) this.label.textContent = busy ? S.adding : this.defaultLabel;
    }

    showError(message) {
      if (!this.error) return;
      const icon = document.querySelector('template[data-icon-alert]');
      this.error.innerHTML = `${icon ? icon.innerHTML : ''}<span>${escapeHTML(message)}</span>`;
      this.error.hidden = false;
    }

    hideError() {
      if (this.error) {
        this.error.hidden = true;
        this.error.textContent = '';
      }
    }
  }

  /* ---------- <sticky-buy> (phones) ---------- */
  class StickyBuy extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form || !('IntersectionObserver' in window)) return;
      this.button = $('[data-sticky-add]', this);
      this.option = $('[data-sticky-option]', this);
      this.price = $('[data-sticky-price]', this);
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.visible = new Set();
      // Hidden while the size picker or the main button is on screen, so it never covers them.
      const watched = [$('variant-picker', this.form), $('[data-buy-area]', this.form)].filter(Boolean);
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.visible.add(entry.target);
            else this.visible.delete(entry.target);
          });
          this.toggle();
        },
        { rootMargin: '0px 0px -40px 0px' }
      );
      watched.forEach((el) => observer.observe(el));
      this.button.addEventListener('click', () => this.onClick());
      document.addEventListener('product:variant', (event) => {
        if (event.detail.formId === this.form.id) this.update(event.detail.variant);
      });
    }

    toggle() {
      const show = this.visible.size === 0 && !this.desktop.matches;
      this.classList.toggle('is-visible', show);
      document.body.classList.toggle('has-sticky-buy', show);
    }

    update(variant) {
      if (this.option) this.option.textContent = variant ? variant.title : S.selectSize;
      if (this.price && variant) this.price.textContent = variant.price;
      const soldOut = !!(variant && !variant.available);
      this.button.disabled = soldOut;
      this.button.textContent = soldOut ? S.soldOut : S.addToCart;
    }

    onClick() {
      const picker = $('variant-picker', this.form);
      if (picker) {
        const variant = picker.variant;
        if (!variant || !variant.available) {
          picker.validate(); // scrolls to the sizes, shows the message, focuses the first size
          return;
        }
      }
      this.form.requestSubmit();
    }
  }

  /* ---------- <quick-add> on product cards ---------- */
  class QuickAdd extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.toggleBtn = $('.quick-add__toggle', this);
      this.panel = $('.quick-add__panel', this);
      this.error = $('[data-quick-error]', this);
      if (!this.toggleBtn) return;
      if (this.toggleBtn.dataset.quickDirect) {
        this.toggleBtn.addEventListener('click', () => this.add(this.toggleBtn.dataset.quickDirect, this.toggleBtn));
        return;
      }
      if (!this.panel || this.toggleBtn.tagName === 'A') return;
      this.toggleBtn.addEventListener('click', () => this.setOpen(this.toggleBtn.getAttribute('aria-expanded') !== 'true'));
      this.panel.addEventListener('click', (event) => {
        const sizeBtn = event.target.closest('[data-variant-id]');
        if (sizeBtn && !sizeBtn.disabled) this.add(sizeBtn.dataset.variantId, sizeBtn);
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) {
          this.setOpen(false);
          this.toggleBtn.focus();
        }
      });
      document.addEventListener('click', (event) => {
        if (this.isOpen && !this.contains(event.target)) this.setOpen(false);
      });
    }

    setOpen(open) {
      this.isOpen = open;
      this.toggleBtn.setAttribute('aria-expanded', String(open));
      this.panel.classList.toggle('is-open', open);
      if (open) {
        const first = $('[data-variant-id]:not(:disabled)', this.panel);
        if (first) first.focus({ preventScroll: true });
      } else if (this.error) {
        this.error.hidden = true;
      }
    }

    async add(variantId, button) {
      if (this.busy) return;
      this.busy = true;
      button.setAttribute('aria-busy', 'true');
      if (this.error) this.error.hidden = true;
      const result = await Cart.add(variantId, 1);
      button.removeAttribute('aria-busy');
      this.busy = false;
      if (!result.ok) {
        const message = errorText(result);
        if (this.error) {
          this.error.textContent = message;
          this.error.hidden = false;
        }
        announce(message);
        return;
      }
      if (this.panel) this.setOpen(false);
      if (!Cart.hasDrawer()) {
        window.location.href = R.cart;
        return;
      }
      Cart.render(result.data.sections);
      Cart.open(this.toggleBtn, Cart.addedMessage(result.data));
    }
  }

  /* ---------- <cart-items> (drawer + cart page) ---------- */
  class CartItems extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-qty-step]');
        if (step) {
          const line = step.closest('.cart-line');
          const input = $('[data-qty-input]', line);
          const next = (parseInt(input.value, 10) || 1) + Number(step.dataset.qtyStep);
          this.update(line, Math.max(1, next));
          return;
        }
        const remove = event.target.closest('[data-remove]');
        if (remove) {
          event.preventDefault();
          this.update(remove.closest('.cart-line'), 0);
        }
      });
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-qty-input]');
        if (input) this.onTyped(input);
      });
      // Enter in a quantity box must not submit the cart form (its first button is Checkout).
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('[data-qty-input]')) {
          event.preventDefault();
          this.onTyped(event.target);
        }
      });
    }

    onTyped(input) {
      const line = input.closest('.cart-line');
      const previous = Number(line.dataset.quantity);
      const value = Number(input.value);
      if (!Number.isInteger(value) || value < 1) {
        input.value = previous;
        this.lineError(line, S.cartError);
        return;
      }
      const max = input.max ? Number(input.max) : null;
      if (max && value > max) {
        input.value = max;
        this.update(line, max, S.cartLimited.split('[q]').join(max));
        return;
      }
      this.update(line, value);
    }

    async update(line, quantity, note) {
      if (!line) return;
      const key = line.dataset.key;
      const previous = Number(line.dataset.quantity);
      if (quantity === previous && !note) return;
      const input = $('[data-qty-input]', line);
      if (CartState.busy) {
        // Remember only the latest wish for this line; it is sent when the current request ends.
        CartState.pending = { key, quantity };
        if (input && quantity > 0) input.value = quantity;
        return;
      }
      CartState.busy = true;
      line.setAttribute('aria-busy', 'true');
      const result = await Cart.change(key, quantity);
      CartState.busy = false;

      if (!result.ok) {
        line.removeAttribute('aria-busy');
        if (input) input.value = previous;
        const message = errorText(result);
        this.lineError(line, message);
        announce(message);
        CartState.pending = null;
        return;
      }

      const item = (result.data.items || []).find((i) => i.key === key);
      Cart.render(result.data.sections);
      let message = quantity === 0 ? S.cartRemoved : S.cartUpdated;
      const fresh = document.querySelector(`.cart-line[data-key="${CSS.escape(key)}"]`);
      if (quantity > 0 && item && item.quantity < quantity) {
        message = S.cartLimited.split('[q]').join(item.quantity);
        if (fresh) this.lineError(fresh, message);
      } else if (note && fresh) {
        this.lineError(fresh, note);
        message = note;
      }
      announce(message);

      // Keep keyboard focus in a sensible place after the re-render.
      if (fresh) {
        const target = $('[data-qty-input]', fresh);
        if (target && document.activeElement === document.body) target.focus({ preventScroll: true });
      } else {
        const heading = document.querySelector('#CartDrawer[open] #CartDrawerTitle') || document.querySelector('.cart-page h1');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }
      }

      if (CartState.pending) {
        const next = CartState.pending;
        CartState.pending = null;
        const nextLine = document.querySelector(`.cart-line[data-key="${CSS.escape(next.key)}"]`);
        const cartItems = nextLine && nextLine.closest('cart-items');
        if (cartItems) cartItems.update(nextLine, next.quantity);
      }
    }

    lineError(line, message) {
      const error = $('[data-line-error]', line);
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    }
  }

  /* ---------- <media-gallery>: progress bars for the swipe gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      const track = $('[data-gallery-track]', this);
      const bars = $$('.gallery__bar', this);
      if (!track || bars.length < 2 || !('IntersectionObserver' in window)) return;
      const slides = $$('[data-slide]', track);
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const index = slides.indexOf(entry.target);
            bars.forEach((bar, i) => bar.classList.toggle('is-active', i === index));
          });
        },
        { root: track, threshold: 0.6 }
      );
      slides.forEach((slide) => observer.observe(slide));
    }
  }

  /* ---------- <collection-filter>: instant type filter + sort ---------- */
  class CollectionFilter extends HTMLElement {
    connectedCallback() {
      const grid = document.querySelector('[data-collection-grid]');
      const count = document.querySelector('[data-collection-count]');
      this.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-filter]');
        if (!chip || !grid) return;
        const filter = chip.dataset.filter;
        $$('[data-filter]', this).forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
        let shown = 0;
        $$(':scope > li', grid).forEach((li) => {
          const match = !filter || li.dataset.type === filter;
          li.hidden = !match;
          if (match) shown += 1;
        });
        if (count) {
          const template = shown === 1 ? count.dataset.one : count.dataset.other;
          count.textContent = template.replace(/\d+/, shown);
          announce(count.textContent);
        }
      });
      const sort = $('[data-sort-form] select', this);
      if (sort) sort.addEventListener('change', () => sort.form.submit());
    }
  }

  /* ---------- <predictive-search> ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('[data-search-input]', this);
      this.results = $('[data-search-results]', this);
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', debounce(() => this.search(), 220));
      // In a search field the browser's first Escape only clears the text; close the sheet instead.
      this.input.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const dialog = this.closest('dialog');
        if (dialog && dialog.open) {
          event.preventDefault();
          dialog.close();
        }
      });
    }

    async search() {
      const query = this.input.value.trim();
      if (this.controller) this.controller.abort();
      if (!query) {
        this.results.innerHTML = '';
        return;
      }
      this.controller = new AbortController();
      const url = `${R.predictiveSearch}?q=${encodeURIComponent(query)}&resources%5Btype%5D=product&resources%5Blimit%5D=6&section_id=${encodeURIComponent(
        this.dataset.sectionId || 'predictive-search'
      )}`;
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const content = parseHTML(await response.text()).querySelector('[data-predictive-results]');
        this.results.replaceChildren(...(content ? [content] : []));
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = '';
      }
    }
  }

  /* ---------- <product-recommendations> ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this._ready) return;
      this._ready = true;
      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(this);
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url);
        if (!response.ok) return;
        const result = parseHTML(await response.text()).querySelector('[data-recommendations-result]');
        const list = $('[data-recommendations-list]', this);
        // Only replace the server-rendered list when Shopify actually returned products.
        if (result && list && result.querySelector('li')) {
          list.replaceChildren(...result.childNodes);
          markLoadedImages(list);
        }
      } catch (error) {
        /* keep the server-rendered list */
      }
    }
  }

  /* ---------- <contact-form>: inline validation, input preserved ---------- */
  class ContactForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.form.noValidate = true;
      const success = $('[data-form-success]', this);
      if (success) success.focus();
      this.form.addEventListener('submit', (event) => {
        const invalid = $$('input[required], textarea[required]', this.form).filter((field) => !this.check(field));
        if (invalid.length) {
          event.preventDefault();
          invalid[0].focus();
        }
      });
      this.form.addEventListener('input', (event) => {
        if (event.target.getAttribute('aria-invalid') === 'true') this.check(event.target);
      });
    }

    check(field) {
      let ok = field.value.trim() !== '';
      if (ok && field.type === 'email') ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value.trim());
      field.setAttribute('aria-invalid', String(!ok));
      const error = document.getElementById(field.getAttribute('aria-describedby'));
      if (error) error.hidden = ok;
      return ok;
    }
  }

  /* ---------- delivery estimate in the cart: "Arrives 22–24 Oct" ---------- */
  // Worked out when the cart is viewed (pages are cached), counting calendar days from today in
  // India time. The day and month names come from the en-IN date format.
  function arrivalDates(minDays, maxDays) {
    const today = {};
    new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', year: 'numeric', month: 'numeric', day: 'numeric' })
      .formatToParts(new Date())
      .forEach((part) => (today[part.type] = Number(part.value)));
    const dayMonth = (offset) => {
      const out = {};
      const date = new Date(Date.UTC(today.year, today.month - 1, today.day + offset));
      new Intl.DateTimeFormat('en-IN', { timeZone: 'UTC', day: 'numeric', month: 'short' })
        .formatToParts(date)
        .forEach((part) => (out[part.type] = part.value));
      return out;
    };
    const from = dayMonth(minDays);
    const to = dayMonth(maxDays);
    if (minDays === maxDays) return `${to.day} ${to.month}`;
    if (from.month === to.month) return `${from.day}\u2013${to.day} ${to.month}`;
    return `${from.day} ${from.month} \u2013 ${to.day} ${to.month}`;
  }

  function showArrival() {
    $$('[data-arrival]').forEach((el) => {
      const a = parseInt(el.dataset.min, 10);
      const b = parseInt(el.dataset.max, 10);
      if (Number.isNaN(a) || Number.isNaN(b) || !S.arrives) return;
      el.textContent = S.arrives.replace('[dates]', arrivalDates(Math.min(a, b), Math.max(a, b)));
      el.hidden = false;
    });
  }
  showArrival();

  /* ---------- image loading placeholders ---------- */
  // A .lqip frame shows ink black and a blurred 20px copy of its photo (CSS) until the photo has
  // loaded; .is-loaded then drops the placeholder, so transparent PNGs keep their usual background.
  function markLoaded(img) {
    const frame = img.closest('.lqip');
    if (frame && frame.querySelector('img') === img) frame.classList.add('is-loaded');
  }
  function markLoadedImages(root = document) {
    $$('.lqip:not(.is-loaded) img', root).forEach((img) => {
      if (img.complete && img.naturalWidth) markLoaded(img);
    });
  }
  document.addEventListener(
    'load',
    (event) => {
      if (event.target.tagName === 'IMG') markLoaded(event.target);
    },
    true
  );
  markLoadedImages();

  /* ---------- gentle reveal for below-the-fold rails ---------- */
  if ('IntersectionObserver' in window && !reduceMotion()) {
    const revealer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          revealer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    $$('.reveal').forEach((el) => revealer.observe(el));
  } else {
    $$('.reveal').forEach((el) => el.classList.add('is-in'));
  }

  /* ---------- register (parents before children) ---------- */
  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };
  define('product-form', ProductForm);
  define('variant-picker', VariantPicker);
  define('sticky-buy', StickyBuy);
  define('quick-add', QuickAdd);
  define('cart-items', CartItems);
  define('media-gallery', MediaGallery);
  define('collection-filter', CollectionFilter);
  define('predictive-search', PredictiveSearch);
  define('product-recommendations', ProductRecommendations);
  define('contact-form', ContactForm);
})();
